<template>
  <div class="region-3d-map-wrap">
    <div v-if="loading" class="map-loading">
      <i class="el-icon-loading"></i>
      <span>地图数据加载中...</span>
    </div>
    <div v-else-if="errorMsg" class="map-error">
      <i class="el-icon-warning-outline"></i>
      <span>{{ errorMsg }}</span>
    </div>
    <div
      ref="mapContainer"
      class="map-container"
      :style="{ width: width, height: height }"
    ></div>
  </div>
</template>

<script>
import echarts from 'echarts'
import 'echarts-gl'

// 已注册的地图名称集合，避免重复注册
const registeredMaps = new Set()
// GeoJSON 缓存
const geoCache = {}

// 常见行政区划 adcode → 名称映射（用于 parent 无 name 时兜底）
const ADCODE_NAME_MAP = {
  '110000': '北京市', '120000': '天津市', '130000': '河北省', '140000': '山西省',
  '150000': '内蒙古自治区', '210000': '辽宁省', '220000': '吉林省', '230000': '黑龙江省',
  '310000': '上海市', '320000': '江苏省', '330000': '浙江省', '340000': '安徽省',
  '350000': '福建省', '360000': '江西省', '370000': '山东省', '410000': '河南省',
  '420000': '湖北省', '430000': '湖南省', '440000': '广东省', '450000': '广西壮族自治区',
  '460000': '海南省', '500000': '重庆市', '510000': '四川省', '520000': '贵州省',
  '530000': '云南省', '540000': '西藏自治区', '610000': '陕西省', '620000': '甘肃省',
  '630000': '青海省', '640000': '宁夏回族自治区', '650000': '新疆维吾尔自治区',
  '710000': '台湾省', '810000': '香港特别行政区', '820000': '澳门特别行政区'
}

// ⚠️ 生产环境建议：将 GeoJSON 数据下载到本地 CDN 或服务器，避免依赖第三方公开接口
const DATAV_API_BASE = process.env.NODE_ENV === 'development'
  ? '/datav-geo/areas_v3/bound'
  : 'https://geo.datav.aliyun.com/areas_v3/bound'

export default {
  name: 'Region3DMap',
  props: {
    // 行政区划代码，如 440300（深圳市）、440306（宝安区）
    adcode: {
      type: [String, Number],
      required: true
    },
    // 地图标题（可选，不传则自动提取）
    title: {
      type: String,
      default: ''
    },
    // 容器宽度
    width: {
      type: String,
      default: '100%'
    },
    // 容器高度
    height: {
      type: String,
      default: '500px'
    },
    // 地图主题色
    themeColor: {
      type: String,
      default: '#aec6f0'
    },
    // 高亮色
    highlightColor: {
      type: String,
      default: '#2b6de8'
    },
    // 是否自动旋转
    autoRotate: {
      type: Boolean,
      default: true
    },
    // 视角距离
    viewDistance: {
      type: Number,
      default: 100
    },
    // 视角俯仰角
    viewAlpha: {
      type: Number,
      default: 35
    },
    // 视角偏航角
    viewBeta: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: false,
      errorMsg: '',
      chart: null,
      mapTitle: ''
    }
  },
  watch: {
    adcode: {
      handler(newVal, oldVal) {
        if (newVal && newVal !== oldVal) {
          this.loadMap()
        }
      }
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initChart()
      if (this.adcode) {
        this.loadMap()
      }
    })
  },
  beforeDestroy() {
    this.cleanup()
    window.removeEventListener('resize', this.handleResize)
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
  },
  methods: {
    /**
     * 初始化 echarts 实例
     */
    initChart() {
      const container = this.$refs.mapContainer
      this.chart = echarts.init(container)
      window.addEventListener('resize', this.handleResize)
      if (typeof ResizeObserver !== 'undefined') {
        this._resizeObserver = new ResizeObserver(() => {
          this.handleResize()
        })
        this._resizeObserver.observe(container)
      }
    },

    /**
     * 尺寸变化重绘
     */
    handleResize() {
      if (this.chart) {
        this.chart.resize()
      }
    },

    /**
     * 从 DataV API 获取 GeoJSON
     */
    async fetchGeoJSON(adcode) {
      const code = String(adcode)
      if (geoCache[code]) return geoCache[code]

      const fetchOptions = process.env.NODE_ENV === 'development' ? {} : { referrerPolicy: 'no-referrer' }
      let geoJSON = null
      let lastError = ''

      // 优先尝试 _full.json（包含下级行政区）
      try {
        const fullRes = await fetch(`${DATAV_API_BASE}/${code}_full.json`, fetchOptions)
        if (fullRes.ok) {
          geoJSON = await fullRes.json()
        } else if (fullRes.status !== 404) {
          lastError = `_full.json HTTP ${fullRes.status}`
        }
      } catch (e) {
        lastError = `_full.json 请求异常: ${e.message || e}`
      }

      // 兜底尝试 .json（单区域）
      if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0) {
        try {
          const simpleRes = await fetch(`${DATAV_API_BASE}/${code}.json`, fetchOptions)
          if (simpleRes.ok) {
            geoJSON = await simpleRes.json()
          } else {
            lastError += ` | .json HTTP ${simpleRes.status}`
          }
        } catch (e) {
          lastError += ` | .json 请求异常: ${e.message || e}`
        }
      }

      if (geoJSON) {
        geoCache[code] = geoJSON
      } else {
        console.warn(`[Region3DMap] 获取 adcode=${code} 数据失败:`, lastError || '无可用数据')
      }
      return geoJSON
    },

    /**
     * 从 GeoJSON 中提取区域名称
     */
    extractRegionName(geoJSON, adcode) {
      if (this.title) return this.title
      const code = String(adcode)
      if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0) {
        return ADCODE_NAME_MAP[code] || code
      }

      const firstFeature = geoJSON.features[0]
      const props = firstFeature.properties || {}

      // 方式1：parent 同时有 adcode 和 name，且 adcode 匹配传入值
      if (props.parent && String(props.parent.adcode) === code && props.parent.name) {
        return props.parent.name
      }

      // 方式2：features 中有 adcode 完全匹配的
      const matched = geoJSON.features.find(
        f => f.properties && String(f.properties.adcode) === code
      )
      if (matched) return matched.properties.name

      // 方式3：parent.adcode 匹配但无 name，用映射表兜底
      if (props.parent && String(props.parent.adcode) === code) {
        return ADCODE_NAME_MAP[code] || `${code}(${props.name || '未知'}所在区域)`
      }

      // 方式4：直接用映射表
      if (ADCODE_NAME_MAP[code]) return ADCODE_NAME_MAP[code]

      // 方式5：fallback 到第一个 feature 的 name
      return props.name || code
    },

    /**
     * 统一加载与渲染 3D 地图
     */
    async loadMap() {
      if (!this.adcode) {
        this.errorMsg = '请提供 adcode 参数'
        return
      }

      this.loading = true
      this.errorMsg = ''

      try {
        const adcode = String(this.adcode)
        const geoJSON = await this.fetchGeoJSON(adcode)

        if (!geoJSON || !geoJSON.features || geoJSON.features.length === 0) {
          this.errorMsg = '未获取到有效的地图边界数据'
          this.loading = false
          return
        }

        this.mapTitle = this.extractRegionName(geoJSON, adcode)

        // 注册 ECharts 地图
        if (!registeredMaps.has(adcode)) {
          echarts.registerMap(adcode, geoJSON)
          registeredMaps.add(adcode)
        }

        // 渲染 3D 视图
        this.renderMap(adcode, this.mapTitle)

        this.$nextTick(() => {
          this.chart && this.chart.resize()
        })

        this.$emit('loaded', { adcode, name: this.mapTitle, geoJSON })
      } catch (e) {
        this.errorMsg = '地图加载出错: ' + (e.message || '未知错误')
        this.$emit('error', e)
      } finally {
        this.loading = false
      }
    },

    /**
     * 渲染 3D 地图核心配置
     */
    renderMap(mapName, regionName) {
      if (!this.chart) return

      const option = {
        backgroundColor: '#BBD5FF',
        title: {
          show: false,
          text: regionName,
          left: 'center',
          top: 10,
          textStyle: {
            color: '#1a3a6b',
            fontSize: 18,
            fontWeight: 'bold'
          }
        },
        tooltip: {
          show: true,
          formatter(params) {
            return params.name ? `<div style="padding:4px 8px;">${params.name}</div>` : ''
          }
        },
        series: [
          {
            type: 'map3D',
            map: mapName,
            shading: 'lambert',
            lambertMaterial: {
              detailTexture: null
            },
            postEffect: {
              enable: true,
              bloom: {
                enable: true,
                bloomIntensity: 0.08
              },
              SSAO: {
                enable: true,
                radius: 2,
                intensity: 1.5,
                quality: 'medium'
              },
              depthOfField: { enable: false }
            },
            light: {
              main: {
                intensity: 1.0,
                shadow: false,
                alpha: 60,
                beta: 10
              },
              ambient: {
                intensity: 0.5
              }
            },
            boxWidth: 140,
            boxHeight: 8,
            boxDepth: 100,
            viewControl: {
              distance: this.viewDistance,
              alpha: this.viewAlpha,
              beta: this.viewBeta,
              autoRotate: this.autoRotate,
              autoRotateAfterStill: 3,
              autoRotateSpeed: 3,
              damping: 0.9,
              rotateSensitivity: 1.2,
              zoomSensitivity: 1.2,
              panSensitivity: 0.8,
              animation: true,
              animationDurationUpdate: 800
            },
            itemStyle: {
              color: this.themeColor,
              opacity: 1,
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.9)'
            },
            emphasis: {
              itemStyle: {
                color: this.highlightColor,
                opacity: 1
              },
              label: {
                show: true,
                textStyle: {
                  fontSize: 14,
                  color: '#ffffff',
                  fontWeight: 'bold'
                }
              }
            },
            label: {
              show: true,
              textStyle: {
                color: '#1a3a6b',
                fontSize: 12,
                fontWeight: '500',
                backgroundColor: 'transparent'
              }
            },
            regionHeight: 3
          }
        ]
      }

      this.chart.setOption(option, true)
    },

    /**
     * 更新视角（外部暴露方法）
     */
    setViewControl(options = {}) {
      if (!this.chart) return
      const currentOption = this.chart.getOption()
      if (currentOption && currentOption.series && currentOption.series[0]) {
        const series = currentOption.series[0]
        series.viewControl = { ...series.viewControl, ...options }
        this.chart.setOption({ series: [series] })
      }
    },

    /**
     * 销毁实例
     */
    cleanup() {
      if (this.chart) {
        this.chart.dispose()
        this.chart = null
      }
    },

    /**
     * 重新加载
     */
    reload() {
      this.loadMap()
    }
  }
}
</script>

<style lang="scss" scoped>
.region-3d-map-wrap {
  position: relative;
  width: 100%;
  background: #BBD5FF;
  border-radius: 8px;
  overflow: hidden;

  .map-container {
    width: 100%;
    display: block;
    box-sizing: border-box;
    overflow: hidden;
  }

  .map-loading, .map-error {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.85);
    z-index: 10;
    font-size: 14px;

    i {
      font-size: 32px;
      margin-bottom: 12px;
    }
  }

  .map-loading {
    color: #666;
    i { color: #409eff; }
  }

  .map-error {
    color: #f56c6c;
  }
}
</style>